import {
  IonBadge,
  IonButton,
  IonCheckbox,
  IonCol,
  IonGrid,
  IonIcon,
  IonLabel,
  IonRow,
  IonSpinner,
  IonText,
  IonSearchbar,
} from '@ionic/react';
import {
  alertOutline,
  caretDownOutline,
  caretUpOutline,
  searchOutline,
} from 'ionicons/icons';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addFile,
  getSavedSelectedTrackMatches,
  getTrackMatches,
  processSelectedFiles,
  saveSelectedTrackFilePaths,
  selectIsTrackLoadingMatches,
  selectAllSelectedMatches,
  selectAllTrackMatches,
  selectShowMatcher,
  selectTrackMatchesCount,
  setSelectedFilePaths,
  setSelectedTrackMatches,
  setTrackMatches,
} from '../../store/slices/track-matcher.slice';
import { iTrack } from '../../types/ITrack';
import { iLocalFile, iLocalFileAPIResponse } from '../../types/ILocalFile';
import isElectron from '../../utils/isElectron';
import { getAppStore } from '../../store/storeRef';
import { mergeTrackMatchFiles } from '../../utils/mergeTrackMatchFiles';
import Pools from '../pools/Pools';
import './Tracks.scss';

import { TrackAvailabilityOptionDto } from '@cratehackers/api-client';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import sha256 from 'crypto-js/sha256';
import { run } from '../../helper/electron';
import { selectSelectedCrateId } from '../../store/slices/crate.slice';
import { AppDispatch, RootState } from '../../store/store';
import { translateKey } from '../../utils/translateKey';
import IntelligentMediaPlayer from '../media-player/IntelligentMediaPlayer';
import { TrackDropdown } from '../trackDropdown/TrackDropdown';
import { TrackPurchaseDropdown } from '../trackDropdown/TrackPurchaseDropdown';
import './Track.scss';
import { TrackDetailsRow } from './TrackDetailsRow';
import { TrackMetadataPanel } from './TrackMetadataPanel';
import { generateTrackID } from '../../utils/generateTrackID';
import { normalizePath } from '../../utils/normalizePath';
import { isPerfectLocalMatch } from '../../utils/isPerfectLocalMatch';
import { resolveMatchDurationLabel } from '../../utils/formatDurationMs';
import { toDurationMs } from '../../utils/toDurationMs';
import { useIcons } from '../../hooks/useIcons';
import { isTrackMatchesLoading } from '../../utils/isTrackMatchesLoading';
import {
  shouldResetTrackMatchFetchState,
  shouldShowNoLocalMatchesEmptyState,
  shouldShowTrackMatchesLoadingState,
} from '../../utils/trackMatchRowFetchState';

// Stable empty array reference to prevent re-renders
const emptyArray: any[] = [];

export interface ElectronFile extends File {
  path: string;
}

const handleDragOver = (event: React.DragEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

// Mixed In Key color scheme for Camelot wheel
const getKeyColor = (camelotKey: string | undefined): string => {
  if (!camelotKey) return 'rgba(255,255,255,0.5)';

  const keyColors: { [key: string]: string } = {
    '1A': '#B8E6B8',
    '1B': '#B8E6B8',
    '2A': '#C8E6A0',
    '2B': '#C8E6A0',
    '3A': '#E6E6A0',
    '3B': '#E6E6A0',
    '4A': '#E6D0A0',
    '4B': '#E6D0A0',
    '5A': '#E6B8A0',
    '5B': '#E6B8A0',
    '6A': '#E6A0B8',
    '6B': '#E6A0B8',
    '7A': '#E6A0D0',
    '7B': '#E6A0D0',
    '8A': '#D0A0E6',
    '8B': '#D0A0E6',
    '9A': '#B8A0E6',
    '9B': '#B8A0E6',
    '10A': '#A0B8E6',
    '10B': '#A0B8E6',
    '11A': '#A0D0E6',
    '11B': '#A0D0E6',
    '12A': '#A0E6E6',
    '12B': '#A0E6E6',
  };

  return keyColors[camelotKey] || 'rgba(255,255,255,0.5)';
};

export const Track: React.FC<
  iTrack & {
    trackNum?: number;
    editable?: boolean;
    initiallyExpanded?: boolean;
    onDelete?: (track: iTrack) => void;
    resourceOptions: TrackAvailabilityOptionDto[];
    streamingOptions: TrackAvailabilityOptionDto[];
    purchaseOptions: TrackAvailabilityOptionDto[];
    cloudStorageOptions: TrackAvailabilityOptionDto[];
    showTrackInfo?: boolean;
    playCountsBySource?: {
      serato: Record<string, { playCount: number }>;
      virtualdj: Record<string, { playCount: number }>;
    };
    sortableId?: string;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
    disableMoveUp?: boolean;
    disableMoveDown?: boolean;
  }
> = (props) => {
  const {
    ID: ExternalSourceId,
    Artist,
    Title,
    BPM,
    duration,
    PreviewURL,
    unmixable,
    editable,
    resourceOptions,
    purchaseOptions,
    streamingOptions,
    cloudStorageOptions,
    playCountsBySource,
    trackNum,
    showTrackInfo = true,
    expanded = false,
    onDelete,
    sortableId,
    onMoveUp,
    onMoveDown,
    disableMoveUp = false,
    disableMoveDown = false,
  } = props;

  // Generate exact trackId from artist and title
  // IMPORTANT: Must use trimmed values to match hash generation in other parts of the app
  const trackId = generateTrackID(props);
  const sortableTrackId = sortableId ?? trackId;

  const dispatch = useDispatch<AppDispatch>();
  const showMatcher = useSelector(selectShowMatcher);
  // Get all track matches once, then extract what we need (avoids inline selector)
  const allMatchedTracks = useSelector(selectAllTrackMatches);
  const matchedTracks = allMatchedTracks?.[trackId];
  const isLoading = useSelector((s: any) =>
    selectIsTrackLoadingMatches(s, trackId),
  );
  const isMatchesLoadingOrPending = isTrackMatchesLoading(
    showMatcher,
    isLoading,
  );
  const crateId = useSelector(selectSelectedCrateId);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [currentDropdown, setCurrentDropdown] = useState('');
  // Lets the async match loader see which tab the user is on after it awaits,
  // so finishing a load never yanks them off Buy/Pools/etc. back to Files.
  const currentDropdownRef = useRef(currentDropdown);
  currentDropdownRef.current = currentDropdown;
  const hasAutoSelectedRef = useRef(false);
  const currentTrackDetails = useRef({
    artist: Artist,
    title: Title,
    trackId,
    count: 0,
  });
  const [isInView, setIsInView] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sortableTrackId, disabled: !editable });
  const [isMetadataPanelOpen, setIsMetadataPanelOpen] = useState(false);
  const key = useSelector((state: RootState) => state.PreferencesState.key);
  const connectedDrives = useSelector(
    (state: RootState) => state.driveState.connectedDrives,
  );

  // Helper function to check if a track's drive is mounted
  const checkIsDriveMounted = (filePath: string): boolean => {
    // Check if path is in user directory (Windows or Unix)
    const isUserDirectory =
      filePath.startsWith('/Users/') || filePath.match(/^[Cc]:/i);

    // User directory files are always accessible
    if (isUserDirectory) {
      return true;
    }

    // Check if the file's path starts with any of the connected drive paths
    return connectedDrives.some((drive) => filePath.startsWith(drive.path));
  };

  // Use memoized selectors to get the whole object, then extract what we need
  const allSelectedMatches = useSelector(selectAllSelectedMatches);
  const allTrackMatches = useSelector(selectAllTrackMatches);
  const selectedPaths = allSelectedMatches[trackId] || emptyArray;
  const existingMatches = allTrackMatches?.[trackId] || emptyArray;
  const [hasFetchedMatches, setHasFetchedMatches] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const trackMatcherCount = useSelector(selectTrackMatchesCount);

  const filteredMatches = useMemo(() => {
    if (!matchedTracks) return undefined;
    if (!searchQuery.trim()) return matchedTracks;

    const query = searchQuery.toLowerCase();
    return matchedTracks.filter((track) => {
      const pathMatch = track.filePath?.toLowerCase().includes(query);
      const titleMatch = track.tags?.title?.toLowerCase().includes(query);
      const artistMatch = track.tags?.artist?.some((a) =>
        a.toLowerCase().includes(query),
      );
      return pathMatch || titleMatch || artistMatch;
    });
  }, [matchedTracks, searchQuery]);

  const { icons } = useIcons();

  // Resolve a matched local file's Serato / VirtualDJ play counts by its path.
  const getPlayCountsForPath = useCallback(
    (filePath?: string) => {
      const key = normalizePath(filePath ?? '').toLowerCase();
      const serato = playCountsBySource?.serato?.[key]?.playCount ?? 0;
      const virtualdj = playCountsBySource?.virtualdj?.[key]?.playCount ?? 0;
      return { serato, virtualdj, total: serato + virtualdj };
    },
    [playCountsBySource],
  );

  // Identify the single most-played matched candidate (so the UI can flag it).
  const mostPlayedMatchId = useMemo(() => {
    if (!matchedTracks?.length) return undefined;
    let bestId: number | undefined;
    let bestTotal = 0;
    matchedTracks.forEach((match) => {
      const { total } = getPlayCountsForPath(match.filePath);
      if (total > bestTotal) {
        bestTotal = total;
        bestId = match.id;
      }
    });
    return bestTotal > 0 ? bestId : undefined;
  }, [matchedTracks, getPlayCountsForPath]);

  // Handles the tracks sliding up and down when a different track is dragged over top
  const sortingAnimationStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Updates the track order of the moving tracks (--is-being-dragged is the track being dragged, all other tracks will either increase or decrease by an order of 1)
    '--track-order':
      transform?.y && trackNum
        ? `var(--is-being-dragged, "${
            transform?.y > 0 ? trackNum + 1 : trackNum - 1
          }")`
        : undefined,
  };

  const trackRef = useRef(null);

  // Update the dropdown handling
  const handleExpandedDropdownChange = (dropdown: string) => {
    // Tracks is handled slightly differently since it uses the isExpanded property
    // It needs an additional property because it can also be controlled by the (show track matcher) toggle at the top of the crate
    if (dropdown === 'tracks') {
      const willExpand = !isExpanded;
      setCurrentDropdown('tracks');
      setIsExpanded(willExpand);
      if (willExpand) {
        loadTrackMatches();
      }
      return;
    } else {
      // If a different dropdown is selected, collapse the tracks dropdown
      setIsExpanded(false);
    }
    setCurrentDropdown(dropdown === currentDropdown ? '' : dropdown);
  };

  // Update the selection handling
  const handleSelectionChange = useCallback(
    (matchId: number, isSelected: boolean) => {
      dispatch(setSelectedTrackMatches({ trackId, matchId, isSelected }));
      const updatedMatches =
        matchedTracks?.map((match) =>
          match.id === matchId ? { ...match, isSelected } : match,
        ) ?? [];
      const selectedPaths = updatedMatches
        .filter((match) => match.isSelected && match.filePath.length > 0)
        .map((match) => normalizePath(match.filePath));
      if (crateId && isElectron()) {
        dispatch(
          saveSelectedTrackFilePaths({
            crateId,
            trackId,
            filePaths: selectedPaths,
          }),
        );
      }
    },
    [dispatch, trackId, matchedTracks, crateId],
  );

  useEffect(() => {
    // if the global "show matcher" is on, or we just selected something, make sure we're expanded
    if (showMatcher) {
      setIsExpanded(true);
      setCurrentDropdown('tracks');
    } else {
      setIsExpanded(false);
      setCurrentDropdown('');
    }
  }, [showMatcher]);

  useEffect(() => {
    if (selectedPaths.length > 0 && showMatcher && !isExpanded) {
      setIsExpanded(true);
      setCurrentDropdown('tracks');
    }
  }, [selectedPaths.length]);

  // Utility function to load track matches
  const loadTrackMatches = useCallback(async (): Promise<boolean> => {
    if (!isElectron()) return false;
    if (matchedTracks !== undefined) {
      return true;
    }

    currentTrackDetails.current = {
      artist: Artist,
      title: Title,
      trackId,
      count: trackMatcherCount,
    };

    const durationMs = toDurationMs(duration);

    const [response, saved] = await Promise.all([
      dispatch(
        getTrackMatches({
          id: trackId,
          artist: Artist,
          title: Title,
          count: trackMatcherCount,
          bpm: BPM,
          durationMs,
        }),
      )
        .unwrap()
        .catch((err) => {
          console.error(`[loadTrackMatches] getTrackMatches failed:`, err);
          return null;
        }),
      crateId
        ? dispatch(getSavedSelectedTrackMatches({ crateId, trackId }))
            .unwrap()
            .catch((err) => {
              console.error(
                `[loadTrackMatches] getSavedSelectedTrackMatches failed:`,
                err,
              );
              return null;
            })
        : Promise.resolve(null),
    ]);

    if (response) {
      const existing =
        getAppStore().getState().trackMatcherState.trackMatches[trackId] || [];
      dispatch(
        setTrackMatches({
          trackId,
          files: mergeTrackMatchFiles(response, existing),
        }),
      );
      if (crateId && saved && saved.length > 0) {
        await dispatch(
          processSelectedFiles({ crateId, trackId, filePaths: saved }),
        ).unwrap();
      }
      return true;
    }

    return false;
  }, [
    Artist,
    Title,
    BPM,
    duration,
    trackId,
    crateId,
    dispatch,
    trackMatcherCount,
    matchedTracks,
  ]);
  useEffect(() => {
    setHasFetchedMatches(false);
  }, [Artist, Title, trackId]);

  useEffect(() => {
    if (shouldResetTrackMatchFetchState(hasFetchedMatches, matchedTracks)) {
      setHasFetchedMatches(false);
    }
  }, [hasFetchedMatches, matchedTracks]);

  const showTrackMatchesLoading = shouldShowTrackMatchesLoadingState(
    matchedTracks,
    hasFetchedMatches,
    isLoading,
  );
  const showNoLocalMatchesEmpty = shouldShowNoLocalMatchesEmptyState(
    matchedTracks,
    hasFetchedMatches,
    isLoading,
  );

  useEffect(() => {
    if (
      isInView &&
      isExpanded &&
      (!hasFetchedMatches ||
        currentTrackDetails.current.count !== trackMatcherCount)
    ) {
      (async () => {
        const loaded = await loadTrackMatches();
        setHasFetchedMatches(true);
        if (!loaded) {
          return;
        }
        const chosenDropdown = currentDropdownRef.current;
        if (chosenDropdown && chosenDropdown !== 'tracks') {
          return;
        }
        setIsExpanded(true);
        setCurrentDropdown('tracks');
      })();
    }
  }, [
    isInView,
    isExpanded,
    hasFetchedMatches,
    trackMatcherCount,
    loadTrackMatches,
  ]);

  useEffect(() => {
    hasAutoSelectedRef.current = false;
  }, [trackId]);
  // TODO: We should remember the selected matched track for each crates track
  // Effect to handle automatic checkbox check based on PreviewURL or 100% match score
  useEffect(() => {
    if (!matchedTracks?.length || hasAutoSelectedRef.current) {
      return;
    }

    if (PreviewURL) {
      const normalizedPreviewURL = normalizePath(PreviewURL);
      const matchingTrack = matchedTracks.find(
        (track) => normalizePath(track.filePath) === normalizedPreviewURL,
      );
      if (matchingTrack && !matchingTrack.isSelected) {
        handleSelectionChange(matchingTrack.id, true);
        hasAutoSelectedRef.current = true;
        return;
      }
    }

    const perfectMatch = (matchedTracks as iLocalFileAPIResponse[]).find(
      (track) => isPerfectLocalMatch(track.match_score),
    );
    if (perfectMatch && !perfectMatch.isSelected) {
      handleSelectionChange(perfectMatch.id, true);
      hasAutoSelectedRef.current = true;
    }
  }, [matchedTracks?.length, PreviewURL]);

  // Dispatches to API to add file and auto-select it
  const addLocalFile = (filePath: string): void => {
    if (!isElectron()) {
      console.warn('File operations require Electron environment');
      return;
    }
    setIsExpanded(true);
    setCurrentDropdown('tracks');
    setHasFetchedMatches(true);
    const normalized = normalizePath(filePath);
    if (!normalized) return;

    const existingMatch = matchedTracks?.find(
      (track) =>
        normalizePath(track.filePath).toLowerCase() ===
        normalized.toLowerCase(),
    );

    if (existingMatch) {
      if (!existingMatch.isSelected) {
        handleSelectionChange(existingMatch.id, true);
      }
      return;
    }

    const newTrack: iLocalFile = {
      filePath: normalized,
      id: new Date().getTime() * filePath.length,
    };
    dispatch(addFile({ file: newTrack, trackId, crateId }));
  };

  const resolvePickerFilePath = (file: File): string | null => {
    const legacyPath = (file as ElectronFile).path;
    if (legacyPath) {
      return normalizePath(legacyPath);
    }
    try {
      const path = window.api?.getPathForFile?.(file);
      return path ? normalizePath(path) : null;
    } catch {
      return null;
    }
  };

  // Handles events from the DOM for adding files (button or drag-n-drop)
  const handleAddFiles = (files: FileList | null): void => {
    if (!files?.length) {
      return;
    }

    Array.from(files).forEach((file) => {
      const filePath = resolvePickerFilePath(file);
      if (!filePath) {
        console.warn(
          '[handleAddFiles] Selected file has no filesystem path; cannot add to track matcher.',
        );
        return;
      }
      addLocalFile(filePath);
    });
  };

  // Keeps track of when the track is in the viewport. Used to only load matches that are on screen
  useEffect(() => {
    const trackElement = trackRef.current;
    if (!trackElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      {
        root: null,
        threshold: 0.1,
      },
    );

    observer.observe(trackElement);

    return () => {
      // Ensure we always disconnect the observer to prevent memory leaks
      // Even if the element is null, disconnect() is safe to call
      observer.disconnect();
    };
  }, []);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (editable) {
      setIsMetadataPanelOpen(true);
    }
  };

  const handleSaveMetadata = (updatedTrack: iTrack) => {
    // TODO: Implement save functionality
    console.log('Saving track metadata:', updatedTrack);
  };

  return (
    <>
      <div
        className={`${trackNum === 1 ? 'track first' : 'track'} ${editable ? 'editable' : ''} ${isExpanded || currentDropdown ? 'expanded' : ''}`}
        ref={setNodeRef}
        style={sortingAnimationStyle}
        onDragOver={handleDragOver}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleAddFiles(event.dataTransfer.files);
        }}
        {...attributes}
      >
        <div
          className={`${unmixable ? 'unmixable' : ''}`}
          data-id={trackId}
          ref={trackRef}
        >
          {showTrackInfo && (
            <TrackDetailsRow
              {...props}
              onDeleteTrack={(event) => {
                onDelete?.(props);
                event.preventDefault();
                event.stopPropagation();
              }}
              dragListeners={listeners}
              onMoveTrackUp={onMoveUp}
              onMoveTrackDown={onMoveDown}
              isMoveUpDisabled={disableMoveUp}
              isMoveDownDisabled={disableMoveDown}
            />
          )}

          {/* Compact Action Tabs - Only show when Track Matcher is enabled */}
          {showMatcher && (
            <div className="track-action-tabs">
              {isElectron() && (
                <button
                  className={`tab-btn ${currentDropdown === 'tracks' && isExpanded ? 'active' : ''}`}
                  onClick={() => handleExpandedDropdownChange('tracks')}
                  disabled={isMatchesLoadingOrPending}
                >
                  Files{' '}
                  {isLoading ? (
                    <IonSpinner name="dots" />
                  ) : matchedTracks?.length ? (
                    `(${matchedTracks.length})`
                  ) : (
                    ''
                  )}
                  <IonIcon
                    icon={
                      currentDropdown === 'tracks' && isExpanded
                        ? caretUpOutline
                        : caretDownOutline
                    }
                  />
                </button>
              )}
              <button
                className={`tab-btn ${currentDropdown === 'pools' ? 'active' : ''}`}
                onClick={() => handleExpandedDropdownChange('pools')}
              >
                Pools
                <IonIcon
                  icon={
                    currentDropdown === 'pools'
                      ? caretUpOutline
                      : caretDownOutline
                  }
                />
              </button>
              <button
                className={`tab-btn ${currentDropdown === 'streaming' ? 'active' : ''}`}
                onClick={() => handleExpandedDropdownChange('streaming')}
              >
                Stream
                <IonIcon
                  icon={
                    currentDropdown === 'streaming'
                      ? caretUpOutline
                      : caretDownOutline
                  }
                />
              </button>
              <button
                className={`tab-btn ${currentDropdown === 'purchase' ? 'active' : ''}`}
                onClick={() => handleExpandedDropdownChange('purchase')}
              >
                Buy
                <IonIcon
                  icon={
                    currentDropdown === 'purchase'
                      ? caretUpOutline
                      : caretDownOutline
                  }
                />
              </button>
              <button
                className={`tab-btn ${currentDropdown === 'resources' ? 'active' : ''}`}
                onClick={() => handleExpandedDropdownChange('resources')}
              >
                Resources
                <IonIcon
                  icon={
                    currentDropdown === 'resources'
                      ? caretUpOutline
                      : caretDownOutline
                  }
                />
              </button>
              <button
                className={`tab-btn ${currentDropdown === 'cloud' ? 'active' : ''}`}
                onClick={() => handleExpandedDropdownChange('cloud')}
              >
                Cloud
                <IonIcon
                  icon={
                    currentDropdown === 'cloud'
                      ? caretUpOutline
                      : caretDownOutline
                  }
                />
              </button>
              {isElectron() && (
                <button
                  className="tab-btn add-local-file-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.multiple = true;
                    input.style.display = 'none';
                    document.body.appendChild(input);

                    const cleanup = () => {
                      if (document.body.contains(input)) {
                        document.body.removeChild(input);
                      }
                      window.removeEventListener('focus', handleFocus);
                    };

                    const handleFocus = () => {
                      setTimeout(cleanup, 1000);
                    };

                    window.addEventListener('focus', handleFocus);

                    input.onchange = (event: Event) => {
                      const target = event.target as HTMLInputElement;
                      handleAddFiles(target.files);
                      cleanup();
                    };

                    input.addEventListener('cancel', cleanup);
                    input.click();
                  }}
                >
                  + Add Local File
                </button>
              )}
            </div>
          )}
        </div>
        {showMatcher && currentDropdown === 'pools' && (
          <Pools artist={Artist} title={Title} trackId={trackId} />
        )}
        {showMatcher && currentDropdown === 'streaming' && (
          <TrackDropdown
            artist={Artist}
            title={Title}
            dropdownData={streamingOptions}
          />
        )}
        {showMatcher && currentDropdown === 'purchase' && (
          <TrackPurchaseDropdown
            artist={Artist}
            title={Title}
            dropdownData={purchaseOptions}
          />
        )}
        {showMatcher && currentDropdown === 'resources' && (
          <TrackDropdown
            artist={Artist}
            title={Title}
            dropdownData={resourceOptions}
          />
        )}
        {showMatcher && currentDropdown === 'cloud' && (
          <TrackDropdown
            artist={Artist}
            title={Title}
            dropdownData={cloudStorageOptions}
          />
        )}
        {showMatcher && isElectron() && (
          <>
            <div
              className={`track-matcher-container ${
                currentDropdown === 'tracks' && isExpanded ? 'open' : ''
              }`}
            >
              {matchedTracks && matchedTracks.length > 0 && showMatcher && (
                <div className="local-files-search">
                  <IonSearchbar
                    value={searchQuery}
                    onIonInput={(e: any) => setSearchQuery(e.detail.value!)}
                    placeholder="Search local files..."
                    className="track-matches-searchbar"
                    style={{
                      padding: '0 8px 8px 8px',
                      '--background': 'var(--ion-color-step-50, #111)',
                      '--color': 'var(--ion-color-step-850, #fff)',
                    }}
                  />
                </div>
              )}
              {filteredMatches && filteredMatches.length > 0 && showMatcher ? (
                filteredMatches.map((track) => {
                  const playCounts = getPlayCountsForPath(track.filePath);
                  const isMostPlayed =
                    mostPlayedMatchId !== undefined &&
                    track.id === mostPlayedMatchId;
                  const matchDuration = resolveMatchDurationLabel(track);
                  return (
                    <div
                      key={track.id + '-' + trackId}
                      className={`matched-track-row${
                        isMostPlayed ? ' most-played' : ''
                      }`}
                    >
                      {/* Play button - Left side */}
                      <div className="matched-track-play">
                        {track.filePath ? (
                          <IntelligentMediaPlayer
                            height={28}
                            url={track.filePath}
                            isDriveMounted={true}
                          />
                        ) : (
                          <span className="no-preview">--</span>
                        )}
                      </div>

                      {/* Checkbox */}
                      <IonCheckbox
                        className="matched-track-checkbox"
                        checked={track.isSelected}
                        onIonChange={(e) => {
                          e.stopPropagation();
                          handleSelectionChange(track.id, e.detail.checked);
                        }}
                      />

                      {/* Track info */}
                      <div className="matched-track-info">
                        <div className="matched-track-title-row">
                          {track.match_score !== undefined && (
                            <span
                              className={`match-score ${
                                track.match_score >= 80
                                  ? 'high'
                                  : track.match_score >= 60
                                    ? 'medium'
                                    : track.match_score >= 40
                                      ? 'low'
                                      : 'poor'
                              }`}
                            >
                              {Math.round(track.match_score)}%
                            </span>
                          )}
                          <span className="matched-track-title">
                            {track.tags?.title && track.tags.title}
                            {!!track.tags?.artist?.length &&
                              track.tags?.title &&
                              ' - '}
                            {!!track.tags?.artist?.length &&
                              track.tags?.artist.join(', ')}
                          </span>
                          {isMostPlayed && (
                            <span
                              className="most-played-flag"
                              title="Most played of your matched files"
                            >
                              Most Played
                            </span>
                          )}
                          {track.detailed_scores &&
                            ((track.detailed_scores.artistScore >= 0.7 &&
                              track.detailed_scores.titleScore <= 0.3) ||
                              (track.detailed_scores.titleScore >= 0.7 &&
                                track.detailed_scores.artistScore <= 0.3)) && (
                              <span
                                className="mismatch-warning"
                                title={`Potential mismatch - Artist: ${Math.round(track.detailed_scores.artistScore * 100)}%, Title: ${Math.round(track.detailed_scores.titleScore * 100)}%`}
                              >
                                !
                              </span>
                            )}
                        </div>
                        <span
                          className="matched-track-path"
                          onClick={() =>
                            run('open-file-in-explorer', {
                              filePath: track.filePath,
                            })
                          }
                        >
                          {track.filePath}
                        </span>
                      </div>

                      {/* Features - Right side */}
                      <div className="matched-track-features">
                        {(playCounts.serato > 0 ||
                          playCounts.virtualdj > 0) && (
                          <div className="matched-feature-cell matched-plays-cell">
                            <span className="feature-label">PLAYS</span>
                            <div className="play-count-badges">
                              {playCounts.serato > 0 && (
                                <span
                                  className="play-count-badge"
                                  title={`Serato play count: ${playCounts.serato}`}
                                >
                                  {icons.seratoDJPro && (
                                    <img
                                      src={icons.seratoDJPro}
                                      alt="Serato"
                                      className="play-count-logo"
                                    />
                                  )}
                                  <span className="play-count-value">
                                    {playCounts.serato}
                                  </span>
                                </span>
                              )}
                              {playCounts.virtualdj > 0 && (
                                <span
                                  className="play-count-badge"
                                  title={`VirtualDJ play count: ${playCounts.virtualdj}`}
                                >
                                  {icons.virtualDJ && (
                                    <img
                                      src={icons.virtualDJ}
                                      alt="VirtualDJ"
                                      className="play-count-logo"
                                    />
                                  )}
                                  <span className="play-count-value">
                                    {playCounts.virtualdj}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="matched-feature-cell matched-key-cell">
                          <span className="feature-label">KEY</span>
                          <span
                            className="feature-value key-value"
                            style={{
                              color: track.tags?.key
                                ? getKeyColor(
                                    translateKey(track.tags.key, 'camelot'),
                                  )
                                : 'rgba(255,255,255,0.5)',
                            }}
                          >
                            {track.tags?.key
                              ? translateKey(track.tags.key, key)
                              : '--'}
                          </span>
                        </div>
                        <div className="matched-feature-cell">
                          <span className="feature-label">BPM</span>
                          <span className="feature-value">
                            {track.tags?.bpm || '--'}
                          </span>
                        </div>
                        <div className="matched-feature-cell matched-duration-cell">
                          <span className="feature-label">LEN</span>
                          <span className="feature-value">
                            {matchDuration ?? '--'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : matchedTracks &&
                matchedTracks.length > 0 &&
                searchQuery.trim() &&
                filteredMatches &&
                filteredMatches.length === 0 ? (
                <div className="no-track-matches">
                  <IonIcon aria-hidden="true" icon={alertOutline} />
                  No local files match your search.
                </div>
              ) : showTrackMatchesLoading ? (
                <div className="no-track-matches">
                  <IonSpinner name="dots" />
                </div>
              ) : showNoLocalMatchesEmpty ? (
                <div className="no-track-matches">
                  <IonIcon aria-hidden="true" icon={alertOutline} />
                  No local files matched. Drag and drop or click Add Local File.
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
      <TrackMetadataPanel
        track={props}
        isOpen={isMetadataPanelOpen}
        onClose={() => setIsMetadataPanelOpen(false)}
        onSave={handleSaveMetadata}
      />
    </>
  );
};
